import Column from '@components/Column';
import Image from '@components/Image';
import Text from '@components/Text';
import Tooltip from '@components/Tooltip';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import useToggleFavoritePrepaidCard from '@hooks/prepaidCard/useToggleFavoritePrepaidCard';
import useToast from '@hooks/ui/useToast';
import FavoriteCircleFill from '@icons/favorite/FavoriteCircleFill';
import { FavoritePrepaidCardRow } from '@models/prepaidCard';
import { Link } from '@stackflow/link';
import { useFlow } from '@stackflow/react';
import { MouseEvent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useRolling from './hooks/useRolling';
import PlusIcon from './PlusIcon';
import Remote from './Remote';

interface Props {
    cards: FavoritePrepaidCardRow[];
    isBlank: boolean;
}

function MultipleCard({ cards, isBlank }:Props) {
    const { t } = useTranslation('prepaidCard');
    const {
        getStyle, moveNext, movePrev, index,
    } = useRolling({ cards });
    const toast = useToast();

    const { handleToggleFavorite } = useToggleFavoritePrepaidCard({ id: cards?.[index]?.id, initialValue: cards?.[index]?.isFavorites || false });
    const handleHeartClick = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        handleToggleFavorite();
        toast.info(t('multipleCard.favoriteRemoved'));
    };
    const { push } = useFlow();
    const containerHeight = useMemo(() => {
        if (cards.length !== 0) {
            return cards.length * 30 + 328;
        }
        return 0;
    }, [cards]);
    return (
        <Container height={containerHeight}
            align='center'
            fullWidth>
            {!isBlank && (
                <Tooltip right='unset'
                    top={24}
                    visibleKey='prepaidCardLog'>
                    {t('multipleCard.tooltip')}
                </Tooltip>
            )}
            {cards.map((card, i) => (
                <CardContainer key={`card ${i}`}
                    style={getStyle(i)}
                    onClick={() => {
                        push('PrepaidCardLogActivity', { prepaidCardId: card.id });
                    }} >
                    <Card>
                        <Image src={card.imageUrl}
                            width={300}
                            height={200}
                            alt={t('multipleCard.imageAlt')}/>
                        {index === i && (
                            <HeartWrapper onClick={handleHeartClick} >
                                <FavoriteCircleFill size={32}/>
                            </HeartWrapper>
                        )}
                    </Card>
                </CardContainer>))}
            {cards.length > 1 && (
                <Remote moveNext={moveNext}
                    movePrev={movePrev} />
            )}
            {!isBlank && (
                <Link activityParams={{ type: 'BUY' }}
                    activityName='PrepaidCardImagesActivity'
                    css={css`
                        align-items: center;
                        bottom: 20px;
                        display: flex;
                        flex-direction: column;
                        position: absolute;
                        row-gap: 6px;
                    `}>
                    <NewIcon>
                        <PlusIcon/>
                    </NewIcon>
                    <Text size='lg' >{t('blank.buyNewCard')}</Text>
                </Link>
            )}
        </Container>
    );
}

const Container = styled(Column)<{height: number}>`
    background-color: var(--color-gray2);
    height: ${(props) => (props.height ? `${props.height}px` : 'unset')};
    max-height: ${(props) => (props.height ? `${props.height}px` : 'unset')};
    min-height: ${(props) => (props.height ? `${props.height}px` : 'unset')};
    position: relative;
`;

const CardContainer = styled.div`
    border-radius: 16px;
    filter: drop-shadow(0px 2.731px 21.844px rgba(0, 0, 0, 0.14));
    height: 200px;
    overflow: hidden;
    position: absolute;
    transform-origin: top;
    transition: 300ms;
    width: 300px;
`;

const Card = styled.div`
    height: 100%;
    position: relative;
    width: 100%;

    img {
        object-fit: cover;
    }
`;

const HeartWrapper = styled.div`
    padding: 2px;
    position: absolute;
    right: 16px;
    top: 16px;
`;

const NewIcon = styled.div`
    align-items: center;
    background-color: var(--color-white);
    border-radius: 15px;
    display: flex;
    height: 30px;
    justify-content: center;
    width: 30px;
`;

export default MultipleCard;
